const MS_PER_DAY = 86400000;

/** Day of the year, 1 on 1 January. */
export function getDayOfYear(date: Date): number {
  const year = date.getFullYear();
  const start = Date.UTC(year, 0, 1);
  const current = Date.UTC(year, date.getMonth(), date.getDate());

  return Math.floor((current - start) / MS_PER_DAY) + 1;
}

/** 365, or 366 in a leap year. */
export function getDaysInYear(year: number): number {
  return (Date.UTC(year + 1, 0, 1) - Date.UTC(year, 0, 1)) / MS_PER_DAY;
}

/** 28, 29, 30 or 31 — the real length of the month, which is what run rate prices. */
export function getDaysInMonth(year: number, month: number): number {
  return (Date.UTC(year, month, 1) - Date.UTC(year, month - 1, 1)) / MS_PER_DAY;
}

/**
 * Days of `month` that fall after the as-of date: none once it has passed the month, the tail of
 * the month holding it, and the whole of a month the year has not reached yet.
 */
export function getRemainingDaysInMonth(year: number, month: number, asOfDate: Date): number {
  const asOfMonth = asOfDate.getMonth() + 1;
  if (month < asOfMonth) {
    return 0;
  }

  return getDaysInMonth(year, month) - (month === asOfMonth ? asOfDate.getDate() : 0);
}

/**
 * YTD ÷ days elapsed — the one number every run-rate figure on the page is built from
 * (TASKID-3398). `null` when there is nothing to divide.
 */
export function getDailyRunRate(ytd: number, asOfDate: Date): number | null {
  if (!Number.isFinite(ytd)) {
    return null;
  }

  const daysInYear = getDaysInYear(asOfDate.getFullYear());
  const daysElapsed = Math.min(Math.max(getDayOfYear(asOfDate), 1), daysInYear);

  return ytd / daysElapsed;
}

/**
 * The AVERAGE row under Actual Production: YTD ÷ days elapsed × the average month length.
 *
 * Days elapsed, not months on file — dividing by whole months counts a part-finished month as a
 * full one and quietly understates the average all month long.
 */
export function getAverageMonthlyProduction(ytd: number, asOfDate: Date): number | null {
  const dailyRunRate = getDailyRunRate(ytd, asOfDate);

  return dailyRunRate === null ? null : dailyRunRate * (getDaysInYear(asOfDate.getFullYear()) / 12);
}

/**
 * Whether the as-of date is inside the last week of its year, at which point the figure on file is
 * treated as the year's total rather than something to project from.
 */
export function isFinalWeekOfYear(date: Date): boolean {
  return getDayOfYear(date) >= getDaysInYear(date.getFullYear()) - 7;
}

/**
 * projected = YTD ÷ days elapsed × days in the year
 *
 * Days, not weeks. The year-to-date figure runs to the last transaction date, which is an ordinary
 * day, so counting whole weeks rounded the elapsed time up to the end of a week the year had not
 * reached and made every projection too low — about 2% in the middle of a month, and far more early
 * in the year, when one extra bucket is a large share of a small denominator. Counting days also
 * annualises over the real 365 or 366 rather than the 364 that 52 weeks actually spans.
 *
 * This is the dashboard's own projection, used for the MGA and RMD views. The company-wide figure
 * comes from the reporting API, whose run rate stays weekly: there the year-to-date really is a whole
 * number of weekly buckets, so weeks are the honest denominator for it.
 */
export function projectAnnualProduction(ytd: number, asOfDate: Date): number | null {
  const dailyRunRate = getDailyRunRate(ytd, asOfDate);

  return dailyRunRate === null ? null : dailyRunRate * getDaysInYear(asOfDate.getFullYear());
}
